/**
 * Kombi — the VW Type 2 van as a reusable, parallaxed brand motif.
 *
 * ORIGIN: a flat-illustration PNG supplied by the designer (a teal split-window Kombi on a sand road,
 * lime sun, teal and ink hills, three trees, four speed streaks). The composition, the palette and the
 * shape language are theirs.
 *
 * WHY THIS IS A REBUILD AND NOT AN <img>. The brief was a parallax with the bus in front of the
 * background, and the supplied asset is ONE flat bitmap in which the bus overlaps the hills. There are
 * no separable depth planes to move, and no amount of CSS gets them out of a raster — a parallax needs
 * the sun, the clouds, the hills, the trees, the road markings and the vehicle to be independent
 * objects. The artwork is flat vector-style shapes, so it reproduces faithfully in SVG, and the rebuild
 * also buys: token-driven colour (see kombi.css), crisp at any size including a 24px inline mark, and
 * roughly 7 KB gzipped against the PNG's 517 KB.
 *
 * EVERY NUMBER IN THIS FILE IS TRACED, NOT DRAWN. The geometry was measured off the flattened bitmap
 * with a pixel probe — column and row scans for each edge, a luminance map for the glass shading, a
 * radial profile through a wheel for its four rings — and verified by compositing the source over the
 * render in `difference` blend mode, where an exact match goes black. The first pass at this component
 * was drawn by eye and lost most of the vehicle's character. If you change a coordinate here, re-run
 * that overlay; do not adjust it to taste.
 *
 * WHY CSS KEYFRAMES AND NOT requestAnimationFrame. Topography.tsx — this app's other decorative
 * backdrop — is a rAF loop, and it had to hand-write a reduced-motion branch, an IntersectionObserver,
 * a visibilitychange handler and a context-loss handler, because a rAF loop bypasses all three layers
 * of the documented motion policy and burns main thread while off screen. Composited CSS transforms
 * need none of that: layer 1 of the policy (design-system.css's @media block) already neutralises
 * them, browsers already throttle off-screen composited animations, and there is no GL context to
 * lose. So this component contains NO effects, NO refs and NO reduced-motion check. That is the point.
 *
 * HOW THE INFINITE SCROLL WORKS. Each moving layer is drawn ONCE into <defs> as a tile, then instanced
 * side by side with <use> and translated by exactly one tile width, at which point instance N+1 sits
 * where instance N began and the seam is invisible. That only holds if nothing crosses a tile edge
 * mid-shape, which is why every hill has BOTH feet planted on the horizon inside its tile (the source
 * lets its hills run off frame, which cannot tile). The road markings get their OWN tile width because
 * their measured pitch of 161 does not divide 1536 — see ROAD_TILE.
 *
 * DELIBERATE DEPARTURES from the supplied PNG. Each is here for a reason; do not "restore" without
 * reading it:
 *
 *  1. HILLS HAVE TWO FEET. See above — a hill running off the frame edge cannot tile, and a visible
 *     repeat seam is worse than a hill with a left flank. Their peak heights and masses are measured;
 *     only the feet moved.
 *
 *  2. THE WHEELS HAVE SPOKES. The source's four rings are perfectly concentric, and a concentric disc
 *     rotating is INVISIBLE — the wheels would spin with no visual evidence and the bus would look like
 *     it was being dragged. Four short spokes in --kombi-rim at 0.4 are the minimum asymmetry that
 *     reads as rolling without inventing an alloy the source does not have.
 *
 *  3. NO WHITE SPEED STREAK. The source's four streaks are ink / white / ink / lime, and the white one
 *     works only because that fixed composition pins it over the teal hill. Here the scenery scrolls,
 *     so the backdrop behind any given streak changes every couple of seconds and a near-white line
 *     would blink in and out of existence. It takes --kombi-sand instead — the one light value in this
 *     palette already proven to read against BOTH backdrops it will cross (it is what makes the clouds
 *     visible on --surface, and it is a cream on a teal hill). --accent was tried first and is wrong
 *     for exactly the same reason white is: it vanishes against the --accent hill it spends half its
 *     time in front of.
 *
 *  4. THE ROAD REACHES THE BOTTOM EDGE. The PNG's sand band stops at y=828 and the strip below it is
 *     transparent — an artefact of how it was exported, not a drawn edge. Running it to the viewBox
 *     floor means a host can sit this flush against anything without a transparent sliver.
 *
 *  5. THE LIME IS THE SOURCE'S OLIVE, NOT THE APP'S. See --kombi-lime in kombi.css: #b2ca1e is what the
 *     artwork actually uses and #b0fe00 is a different colour. Overridable in one line.
 *
 * WHAT IS *NOT* A DEPARTURE, because the first pass got each of these wrong and they are worth naming
 * so they are not "simplified" back: the V-nose is a tapering wedge that pinches out at (1066, 562),
 * not a vertical cream/teal split; there are SIX glass openings at six different widths, not five equal
 * ones; each pane's diagonal shading is DARKER and on the left, not lighter and on the right; the tyre
 * is r=48 inside an r=54 arch cut, not an r=42 disc under a flat sill; the wheel has a muted olive ring
 * between its cream disc and its lime hub; and the roof carries a drip rail at y=398.
 *
 * ACCESSIBILITY. Decorative by default: aria-hidden, focusable="false", so it is absent from the
 * accessibility tree and carries no information that is lost when it does not render. Pass `title` to
 * make a specific instance a labelled image instead (role="img" + <title>) — appropriate where the
 * illustration IS the content of an empty state and nothing beside it says so.
 */
import { useId, type CSSProperties } from 'react';
import './kombi.css';

/**
 * `drive` — the world rolls, the bus holds station, forever. The default.
 * `arrive` — the same rolling world, plus a ONE-SHOT entrance: the bus drives in from off-frame left
 *            and parks. For a moment that has just happened and will not happen again.
 * `still`  — one static frame, no animation. Byte-for-byte what reduced motion renders.
 */
export type KombiMotion = 'drive' | 'arrive' | 'still';

/**
 * `scene` — the full landscape: sun, clouds, three hills, three trees, road, streaks, bus.
 * `band`  — a SHORT, WIDE strip for a header or a divider: the trees, the speed streaks and the bus
 *           with its wheels turning, on a transparent background. No road, no hills, no sun, no clouds
 *           and no ground shadow, and the bus sits on the strip's bottom edge. This exists because
 *           cropping `scene` into a ~150px band does not work — at that aspect ratio there is no room
 *           for both the road and the hills, so the crop eats the sky and then the roof. Rather than
 *           degrade, `band` re-composes: it is two tiles wide instead of one, which makes the bus
 *           smaller relative to the strip and leaves the parallax legible. See BAND_VIEWBOX.
 * `bus`   — the vehicle and its shadow alone, cropped tight. For a small inline mark where a whole
 *           landscape would be illegible: a heading adornment, an empty-state glyph, a busy indicator.
 */
export type KombiVariant = 'scene' | 'band' | 'bus';

export interface KombiProps {
  variant?: KombiVariant;
  motion?: KombiMotion;
  /**
   * Multiplier on the world's scroll rate. 1 is the tuned default; 0.5 is a drift, 2 is a hurry. Every
   * layer divides by it, including the geared wheels, so the parallax ratios and the roll hold at any
   * value. Clamped below — a zero or negative value would divide by zero in the CSS calc() chain and
   * invalidate every duration at once.
   */
  speed?: number;
  /**
   * Park the van beside a parking sign. For the END of a journey — the state where the point is that
   * the traveller has ARRIVED, not merely that the picture is not moving. A still van and a driving van
   * are the same drawing, so without the sign a finished journey looks like a stalled one.
   *
   * PARKED STOPS THE WORLD, and it has to. In this composition the van holds station while the scenery
   * scrolls past, which is what says the van is moving — so anything pinned to the frame travels WITH
   * the van relative to that scenery, and a sign pinned beside a still-driving van reads as a sign
   * being towed along behind it. (That is exactly what the first version of this did.) So `parked`
   * gates off every scroll, the suspension bob, the endless wheel roll and the speed streaks — see
   * kombi.css. A sign belongs to the world; the honest way to say the van has reached it is that the
   * world has stopped.
   *
   * Composes with `motion`: `still` + `parked` is the resting "you got there" frame. `arrive` +
   * `parked` is the van pulling into the space — the one thing that still moves is the arrival itself,
   * carried by a FINITE two-turn wheel roll that settles square.
   *
   * On `variant="bus"` this widens the crop to take the sign in; scene and band already have the room.
   */
  parked?: boolean;
  className?: string;
  /**
   * Set this ONLY when the illustration carries meaning that no adjacent text carries. It turns the
   * svg into role="img" with an accessible name; leaving it unset keeps the motif decorative, which is
   * correct for a banner sitting above a heading that already says the same thing.
   */
  title?: string;
  /**
   * Retint THIS instance. Every colour in the illustration is a custom property on the root, so a
   * caller can move the whole thing onto a different part of the palette without a second copy of the
   * geometry:
   *
   *   tint={{ '--kombi-body': 'var(--accent)', '--kombi-lime': 'var(--decor-lime)' }}
   *
   * Typed to the nine properties that exist rather than accepting any style, so a typo is a compile
   * error and the overridable surface is self-documenting. Two uses it is meant for: a staged/progress
   * treatment where several vans appear at once and identical ones would read as a repeat, and a
   * "spent" or "not yet reached" state on the ink opacity ramp (--border-subtle / --border-control).
   *
   * KEEP VALUES POINTED AT TOKENS, not literals. The palette audit at the top of kombi.css only stays
   * true if overrides come from the design system too.
   */
  tint?: KombiTint;
}

/** The overridable colour surface. See KombiProps.tint and the palette audit in kombi.css. */
export type KombiTint = Partial<
  Record<
    | '--kombi-shell'
    | '--kombi-sand'
    | '--kombi-body'
    | '--kombi-glass'
    | '--kombi-glass-shade'
    | '--kombi-rim'
    | '--kombi-tyre'
    | '--kombi-sign'
    | '--kombi-lime'
    | '--kombi-pine'
    | '--kombi-shadow',
    string
  >
>;

/* ── Geometry ────────────────────────────────────────────────────────────────────────────────────────
   All coordinates are USER UNITS matching SOURCE PIXELS: the PNG is 1536 wide and the scene viewBox is
   1536 wide, so every number below can be checked against the bitmap directly. The viewBox starts at
   y=160 because the PNG's top 160 rows are empty sky; trimming them takes the box from 1.79:1 to a
   2.19:1 band, which is the shape a banner actually wants. */

/** The horizon: hill feet, tree trunks and the top of the road band. Measured — the transition sits at
    y=640..642 across the whole width. */
const HORIZON = 641;
/** The viewBox floor. The road runs to it — see departure 4. (The source's sand stops at 828.) */
const FLOOR = 860;
/** One tile for the sky, hill and tree layers. Equal to the viewBox width. */
const TILE = 1536;

/**
 * THE ROAD MARKINGS GET THEIR OWN TILE. Measured pitch is 161 with a 77-unit dash, and 1536/161 = 9.54
 * — not an integer, so a 1536 tile would put a short dash or a long gap at every seam. Rather than
 * round the pitch to 128 or 192 (a 20% error in the most rhythmic element on screen), the dash layer
 * tiles at 10 x 161 = 1610 and scrolls by that instead. Two instances cover the frame throughout the
 * cycle. This is why kombi.css carries a second scroll keyframe.
 */
const ROAD_TILE = 1610;
const DASH_PITCH = 161;
const DASH_COUNT = ROAD_TILE / DASH_PITCH;
const DASH_W = 77;
const DASH_Y = 724;
const DASH_H = 9;

/* --- The bus. Traced; see the file header. ------------------------------------------------------- */

/** Both wheel centres sit on this line. The tyre's radial profile is symmetric about y=650. */
const AXLE_Y = 650;
const WHEEL_REAR_X = 590;
const WHEEL_FRONT_X = 932;
/**
 * Tyre radius 48, inside a body arch cut at 54. The first pass used 42 with no arch at all, which is
 * why the wheels read as too small: a disc tucked under a flat sill looks like a caster, where the same
 * disc filling a cut-out arch looks like a wheel. The 6-unit difference between the two is the dark
 * wheel well, and it is most of the effect.
 */
const WHEEL_R = 48;
const ARCH_R = 54;
/** The wheel's four rings, outside in: tyre, cream disc, olive ring, lime hub. */
const RIM_R = 36;
const OLIVE_R = 23;
const HUB_R = 16;

/** The glass band: every side window and the windscreen share this top and bottom. */
const GLASS_TOP = 410;
const GLASS_BOTTOM = 473;

/** Where cream meets teal down the flank. Measured at y=488 across the whole length. */
const BELT_Y = 488;

/**
 * SIX glass openings as [x, width], and they are NOT a uniform ramp — that was the first pass's
 * mistake. Measured at y=440: 62 / 83 / 80 / 81 / 52 / 43. The narrow fifth is the door's quarter-light
 * and the 43 is the windscreen; five equal panes lost both, and with them the reason the cab end of the
 * bus looks different from the passenger end.
 */
const GLASS: readonly (readonly [number, number])[] = [
  [533, 62],
  [613, 83],
  [714, 80],
  [811, 81],
  [909, 52],
  [972, 43],
];

/**
 * The rear panel's air louvres: SIX lines, x 506..557, at an exact pitch of 12 from y=500. Measured by
 * scanning a column through the block and reading off the dark runs — the first pass guessed seven at a
 * pitch of 7 and drew them half the width they should be.
 */
const LOUVRES = [0, 1, 2, 3, 4, 5].map((i) => 500 + i * 12);
const LOUVRE_X1 = 506;
const LOUVRE_X2 = 557;

/**
 * THE V-NOSE — the Kombi's signature, and the single biggest thing missing from the first pass, which
 * split cream from teal on a straight vertical line.
 *
 * What the source actually does: the cream sweeps down off the roof at the front and tapers to a POINT,
 * leaving teal on both sides of it lower down. Traced boundary (the rightmost teal pixel per row):
 *
 *     y  492 → x  984      y  532 → x 1049
 *     y  500 → x 1011      y  540 → x 1054
 *     y  508 → x 1023      y  548 → x 1059
 *     y  516 → x 1034      y  556 → x 1064
 *     y  524 → x 1044      y  562 → the wedge closes
 *
 * and the vehicle's own front edge over the same rows runs 1054 → 1070. So the wedge is 63 units wide
 * at the belt line and pinches out at (1066, 562); below that the front is teal all the way to the
 * front edge, with the headlight floating in it as a cream island. Both curves are expressed as cubics
 * in the two paths below rather than as this table of points.
 */
const NOSE_TIP_X = 1066;
const NOSE_TIP_Y = 562;
/** The vehicle's front and rear edges. Measured; the rear leans in very slightly. */
const FRONT_X = 1070;
const REAR_X = 474;
/** The vehicle's roofline and the bottom of its tyres — the band variant's content bounds. */
const BUS_TOP = 372;
const BUS_FLOOR = AXLE_Y + WHEEL_R;

/**
 * The four speed streaks: [x1, x2, y, fill]. Read left-to-right off the PNG. The second is
 * --kombi-sand rather than the source's white — see departure 3.
 */
const STREAKS: readonly (readonly [number, number, number, string])[] = [
  [277, 430, 517, 'var(--ink)'],
  [202, 388, 545, 'var(--kombi-sand)'],
  [140, 362, 573, 'var(--ink)'],
  [272, 415, 601, 'var(--kombi-lime)'],
];

/* --- The parking sign (`parked`) ------------------------------------------------------------------
   NOT from the source PNG — there is no sign in it. Drawn in the same flat-vector language: one post,
   one rounded plate, one letter, all on the illustration's own tokens. It exists because "the journey
   is finished" needs to be legible in the picture and not only in the copy: a stationary van and a
   driving van are the same drawing, so an arrived van beside a parking sign is the only way the still
   frame says "arrived" rather than "stopped".

   Standing on the SAME ground line as the tyres (BUS_FLOOR) and clear of the front bumper's 1082.
   PROPORTIONS ARE FROM LIFE, and they matter because the first attempt got them wrong: a parking plate
   is about 600mm on a post reaching ~2.1m, against a Kombi's 1.9m of height. So the plate is 100 units
   — a shade under a third of the vehicle's 326 — and its top sits just ABOVE the roofline rather than
   below it. At 78 units the plate was 24% of the van and the letter was illegible by the time the whole
   thing was scaled to a 44px progress indicator, which is the size that actually has to work. */
const SIGN_PLATE_X = 1110;
const SIGN_PLATE_Y = 368;
const SIGN_PLATE_SIZE = 100;
const SIGN_POST_W = 10;
/** The post is centred under the plate, so it is derived rather than stated twice. */
const SIGN_POST_X = SIGN_PLATE_X + SIGN_PLATE_SIZE / 2 - SIGN_POST_W / 2;
/** The plate's centre — where the letter sits. */
const SIGN_CX = SIGN_PLATE_X + SIGN_PLATE_SIZE / 2;
const SIGN_CY = SIGN_PLATE_Y + SIGN_PLATE_SIZE / 2;
/** The sign's right edge, which is what widens the `bus` crop when parked. */
const SIGN_RIGHT = SIGN_PLATE_X + SIGN_PLATE_SIZE;

const SCENE_VIEWBOX = `0 160 ${TILE} ${FLOOR - 160}`;
/** Tight crop around the vehicle for variant="bus": x 448..1096, y 360..712 (shadow included). */
const BUS_VIEWBOX = '448 360 648 352';
/** The same crop widened to take the sign in. Only `bus` needs it — scene and band have room already. */
const BUS_PARKED_VIEWBOX = `448 360 ${SIGN_RIGHT + 16 - 448} 352`;

/**
 * THE BAND'S BOX. Vertically it is exactly the content: the pine's apex (286) down to the bottom of the
 * tyres (698), so the bus stands on the strip's floor with nothing below it and no wasted sky above.
 *
 * Horizontally it is TWO tiles, not one, and that is the whole trick. A band is ~8:1; the scene's box is
 * 2.19:1, so cropping the scene to a band throws away the sky and then the roof. Doubling the width
 * instead makes one user unit smaller relative to the host, so the same measured bus renders at roughly
 * half the height and fits — 3072/412 = 7.5:1, which at 1200px wide is a 165px strip. The bus shrinks;
 * nothing is cropped.
 */
const BAND_TOP = 286;
const BAND_TILES = 2;
const BAND_W = TILE * BAND_TILES;
const BAND_VIEWBOX = `0 ${BAND_TOP} ${BAND_W} ${BUS_FLOOR - BAND_TOP}`;
/** Shift that centres the bus in the doubled box. The vehicle's own centre sits at 772. */
const BAND_BUS_SHIFT = BAND_W / 2 - 772;

/* ── Layer tiles ─────────────────────────────────────────────────────────────────────────────────────
   Each of these renders ONE tile. They live in <defs> and are instanced by <use>; nothing here
   positions itself for the loop, and nothing here may cross a tile edge mid-shape. */

/**
 * Two clouds. Each is a flat-bottomed pill with two lumps on it — three overlapping shapes in one fill,
 * which is how the source draws them and how flat illustration unions shapes generally. Measured bounds:
 * left x 342..497 y 238..293, right x 1060..1245 y 218..286. Both sit well inside the tile edges so
 * neither straddles a seam.
 */
function CloudTile() {
  return (
    <g fill="var(--kombi-sand)">
      <g>
        <rect x="342" y="275" width="155" height="18" rx="9" />
        <circle cx="400" cy="266" r="28" />
        <circle cx="458" cy="275" r="18" />
      </g>
      <g>
        <rect x="1060" y="268" width="185" height="18" rx="9" />
        <circle cx="1125" cy="250" r="32" />
        <circle cx="1198" cy="264" r="22" />
      </g>
    </g>
  );
}

/**
 * Three hills, drawn far-to-near so the nearer ink mass overlaps the pine one. Peaks are measured (the
 * teal crests at y=362, the ink mass at y=330); only the feet were moved inboard so the layer tiles —
 * departure 1. Every apex is a quadratic control point placed so the curve's midpoint lands on the
 * measured height: a Q curve peaks at the 1:2:1 weighted average of its endpoints and control, so a 362
 * apex between two 415-high shoulders needs a control at 310, not at 362.
 */
function HillTile() {
  return (
    <>
      {/* A low foothill on the massif's LEFT shoulder, so the three depth steps are all visible. On its
          right it would be buried under the ink mass and only its last 30 units would show. */}
      <path
        d={`M 830 ${HORIZON} L 900 540 Q 936 498 972 542 L 1055 ${HORIZON} Z`}
        fill="var(--kombi-pine)"
      />
      {/* The massif. Measured: the dark mass spans x 950..1536 and crests at y=330. Its WIDTH is
          load-bearing — an earlier pass drew the same height across 460 units instead of 550 and the
          hill came out as a dark spike, which is the one thing in this landscape that reads as a
          mistake rather than as a mountain. */}
      <path
        d={`M 950 ${HORIZON} L 1120 400 Q 1215 250 1310 410 L 1500 ${HORIZON} Z`}
        fill="var(--ink)"
      />
      <path
        d={`M 0 ${HORIZON} L 118 415 Q 156 310 194 415 L 488 ${HORIZON} Z`}
        fill="var(--accent)"
      />
    </>
  );
}

/**
 * The pine and the two round trees. Measured: pine apex (1302, 286) on a base spanning x 1200..1405;
 * teal canopy centre (1208, 534) r 57; lime canopy centre (1426, 462) r 62. The first pass had all three
 * canopies ~20% too small, which is what made the right-hand group read as shrubs.
 *
 * `groundY` is where the trunks stop. The scene puts them on the horizon; the band has no ground at all,
 * so it runs them to the strip's floor instead — a trunk that stops in mid-air reads as a floating tree.
 */
function TreeTile({ groundY }: { groundY: number }) {
  return (
    <>
      <path d="M 1302 286 L 1405 566 L 1200 566 Z" fill="var(--kombi-pine)" />
      <rect x="1299" y="550" width="7" height={groundY - 550} fill="var(--kombi-pine)" />

      <rect x="1204" y="520" width="7" height={groundY - 520} fill="var(--kombi-pine)" />
      <circle cx="1208" cy="534" r="57" fill="var(--accent)" />

      <circle cx="1426" cy="462" r="62" fill="var(--kombi-lime)" />
      {/* Trunk and branch drawn OVER the lime canopy, which is how the source has it — the dark line
          crossing the disc is visible in the PNG and is most of what makes that tree read as a tree.
          The branch starts below the trunk's top so the two form a Y rather than a V. */}
      <rect x="1423" y="440" width="7" height={groundY - 440} fill="var(--kombi-pine)" />
      <path
        d="M 1426 508 L 1466 468"
        stroke="var(--kombi-pine)"
        strokeWidth="6"
        strokeLinecap="round"
        fill="none"
      />
    </>
  );
}

/** The centre line: ten 77-unit dashes at the measured pitch of 161. Tiles at ROAD_TILE, not TILE. */
function DashTile() {
  return (
    <g fill="var(--surface)">
      {Array.from({ length: DASH_COUNT }, (_, i) => (
        <rect
          key={i}
          x={i * DASH_PITCH}
          y={DASH_Y}
          width={DASH_W}
          height={DASH_H}
          rx={DASH_H / 2}
        />
      ))}
    </g>
  );
}

/**
 * The parking sign. See the geometry block above for why it exists and where it stands.
 *
 * RENDERED OUTSIDE `.tb-kombi__enter`, BY Kombi RATHER THAN BY Bus — deliberately. Under
 * `motion="arrive"` the enter group slides in from off-frame left, and a sign that slid in alongside
 * the van would be nonsense: the sign is part of the world, the van is the thing arriving at it.
 *
 * BEING A SIBLING IS NECESSARY BUT NOT SUFFICIENT, which is the subtle bit and the bug the first
 * version shipped. Sitting outside the enter group keeps the sign still IN THE FRAME — but in a
 * composition whose whole grammar is "van holds station, world scrolls past", a thing that is still in
 * the frame is a thing moving along WITH the van. The sign only reads as planted once the scenery stops
 * too, which is why `parked` gates off every scrolling layer rather than just repositioning this one.
 *
 * The letter is a real <text> in the app's own typeface rather than a hand-built path. Inter 500 is
 * loaded at app root by design-system.css (@fontsource/inter/latin-500), so this is not relying on a
 * system fallback, and a glyph beats a bezier nobody can correct later.
 */
function ParkingSign({ shadow }: { shadow: boolean }) {
  return (
    <g>
      {shadow ? (
        <ellipse cx={SIGN_CX} cy={BUS_FLOOR} rx="26" ry="6" fill="var(--kombi-shadow)" />
      ) : null}
      <rect
        x={SIGN_POST_X}
        y={SIGN_PLATE_Y + SIGN_PLATE_SIZE - 10}
        width={SIGN_POST_W}
        height={BUS_FLOOR - (SIGN_PLATE_Y + SIGN_PLATE_SIZE - 10)}
        fill="var(--kombi-pine)"
      />
      <rect
        x={SIGN_PLATE_X}
        y={SIGN_PLATE_Y}
        width={SIGN_PLATE_SIZE}
        height={SIGN_PLATE_SIZE}
        rx="16"
        fill="var(--kombi-sign)"
      />
      <text
        x={SIGN_CX}
        y={SIGN_CY}
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily="var(--font-sans)"
        fontSize="66"
        fontWeight="500"
        fill="var(--kombi-shell)"
      >
        P
      </text>
    </g>
  );
}

/** The four speed streaks. Part of the BUS's frame of reference, not the world's, so they never scroll. */
function Streaks() {
  return (
    <g strokeWidth="9" strokeLinecap="round">
      {STREAKS.map(([x1, x2, y, stroke]) => (
        <path
          key={y}
          className="tb-kombi__streak"
          d={`M ${x1} ${y} H ${x2}`}
          stroke={stroke}
          fill="none"
        />
      ))}
    </g>
  );
}

/* ── The vehicle ─────────────────────────────────────────────────────────────────────────────────────
   Not a tile: the bus never scrolls. It bobs on its suspension and its wheels turn, and under
   motion="arrive" the whole thing slides in once. */

/**
 * One wheel, drawn CENTRED ON ITS OWN ORIGIN and positioned by the parent's translate. That is what
 * makes the rotation safe without relying on transform-origin resolving a particular way — the group's
 * local origin and its bounding-box centre are the same point. See kombi.css's note on `.tb-kombi__wheel`.
 *
 * Four rings, measured off a radial profile through the source's rear wheel: tyre 48, cream disc 36,
 * olive 23, lime hub 16. The olive ring is the one a redraw-by-eye drops, and dropping it is what makes
 * a wheel look like a target.
 */
function Wheel({ cx }: { cx: number }) {
  return (
    <g transform={`translate(${cx} ${AXLE_Y})`}>
      <g className="tb-kombi__wheel">
        <circle r={WHEEL_R} fill="var(--kombi-tyre)" />
        <circle r={RIM_R} fill="var(--kombi-shell)" />
        {/* Departure 2: the spokes exist so the rotation is visible at all. Four is enough asymmetry to
            read as rolling; more starts to look like an alloy the source does not have. */}
        <g stroke="var(--kombi-rim)" strokeWidth="4" strokeLinecap="round" opacity="0.4">
          <path d={`M 0 ${-RIM_R + 2} V ${-OLIVE_R - 1}`} />
          <path d={`M 0 ${RIM_R - 2} V ${OLIVE_R + 1}`} />
          <path d={`M ${-RIM_R + 2} 0 H ${-OLIVE_R - 1}`} />
          <path d={`M ${RIM_R - 2} 0 H ${OLIVE_R + 1}`} />
        </g>
        <circle r={OLIVE_R} fill="var(--kombi-rim)" />
        <circle r={HUB_R} fill="var(--kombi-lime)" />
      </g>
    </g>
  );
}

/**
 * The vehicle. `clipId` must be unique per DOCUMENT — it names the clip that trims the glass and the
 * drip rail to the shell, and two buses on one page sharing it would both clip to whichever mounted
 * first. Threaded in from Kombi's useId rather than generated here, so every variant shares one scheme.
 *
 * `shadow` is off for the band variant: a cast shadow with no ground under it is a shadow on nothing.
 */
function Bus({ clipId, shadow = true }: { clipId: string; shadow?: boolean }) {
  /**
   * The cream shell, as ONE closed path: up the rear curve, across the very slightly domed crown, down
   * the front curve to the belt line, then on DOWN into the V-nose wedge and back up its inner edge to
   * close along the belt. Traced — see the NOSE_TIP note above for the boundary the last two curves
   * follow.
   */
  const shell =
    `M 476 ${BELT_Y} ` +
    // rear pillar, curving up and in to the roof
    `C 476 452 486 412 512 391 C 528 378 560 374 600 373 ` +
    // the crown: a 250-unit near-flat run, 1 unit of dome
    `L 850 ${BUS_TOP} C 905 372 938 374 958 377 ` +
    // down the front, over the windscreen, to the belt
    `C 984 381 1000 391 1010 410 C 1024 437 1038 466 1049 ${BELT_Y} ` +
    // BELOW the belt: out to the front edge and down to the wedge's point
    `C 1058 506 1064 532 ${NOSE_TIP_X} ${NOSE_TIP_Y} ` +
    // back up the wedge's inner edge — the concave sweep that makes it a V-nose
    `C 1056 536 1032 512 986 ${BELT_Y} Z`;

  /**
   * The teal flank. Its top is the belt line, its right follows the nose boundary down and then the
   * vehicle's front edge, and its BOTTOM carries the two arch cut-outs — `A ${ARCH_R}` sweeping over
   * each axle. sweep-flag 0 because the path runs right-to-left along the floor, and 0 is the direction
   * that bulges the arc UP into the body rather than down onto the road.
   */
  const flank =
    `M ${REAR_X} ${BELT_Y} H 986 ` +
    `C 1032 512 1056 536 ${NOSE_TIP_X} ${NOSE_TIP_Y} ` +
    `C 1069 580 ${FRONT_X} 602 ${FRONT_X} 623 ` +
    // the front bumper's notch
    `H 1031 L 1027 ${AXLE_Y} ` +
    `H ${WHEEL_FRONT_X + ARCH_R} A ${ARCH_R} ${ARCH_R} 0 0 0 ${WHEEL_FRONT_X - ARCH_R} ${AXLE_Y} ` +
    `H ${WHEEL_REAR_X + ARCH_R} A ${ARCH_R} ${ARCH_R} 0 0 0 ${WHEEL_REAR_X - ARCH_R} ${AXLE_Y} ` +
    // the rear bumper's notch
    `H 497 L 493 624 H ${REAR_X} Z`;

  return (
    <>
      <defs>
        <clipPath id={clipId}>
          <path d={shell} />
        </clipPath>
      </defs>

      {/* Ground shadow. Outside the bob group on purpose: a shadow that rises and falls with the body
          is a body floating, where a shadow that stays put is a body moving over it. */}
      {shadow ? <ellipse cx="785" cy="698" rx="305" ry="9" fill="var(--kombi-shadow)" /> : null}

      <g className="tb-kombi__bob">
        {/* The wheel wells, BEFORE the flank so the arch cut-outs reveal them. Each is a half-disc above
            its axle only, not a full circle: a full circle would ring the tyre with 6 units of ink below
            the axle too, and the source has none there — the tyre is the outermost thing at the bottom. */}
        <g fill="var(--kombi-tyre)">
          <path
            d={`M ${WHEEL_REAR_X - ARCH_R} ${AXLE_Y} A ${ARCH_R} ${ARCH_R} 0 0 1 ${WHEEL_REAR_X + ARCH_R} ${AXLE_Y} Z`}
          />
          <path
            d={`M ${WHEEL_FRONT_X - ARCH_R} ${AXLE_Y} A ${ARCH_R} ${ARCH_R} 0 0 1 ${WHEEL_FRONT_X + ARCH_R} ${AXLE_Y} Z`}
          />
          {/* The rocker line: a sliver of dark under the floor, measured at 3-4 units. Where it crosses
              an arch it is hidden by the tyre, which is the same colour anyway. */}
          <rect x="497" y={AXLE_Y} width={1027 - 497} height="3.5" />
        </g>

        <path d={shell} fill="var(--kombi-shell)" />
        <path d={flank} fill="var(--kombi-body)" />

        {/* GLASS AND THE DRIP RAIL ARE CLIPPED TO THE SHELL. Both are drawn generously — the windscreen
            runs past the shell's front curve and the drip rail is a straight bar across a domed roof —
            and the clip trims them to the body instead of either being hand-fitted to a curve. That
            keeps the traced numbers exactly as measured rather than nudged to stop them poking out. */}
        <g clipPath={`url(#${clipId})`}>
          {/* The roof's drip rail: a darker cream line under the roof overhang at y 398..402. Small, and
              the thing that stops the upper body reading as one undifferentiated cream slab. */}
          <rect x="500" y="398" width="512" height="4.5" fill="var(--kombi-sand)" />

          {GLASS.map(([x, w]) => (
            <g key={x}>
              <rect
                x={x}
                y={GLASS_TOP}
                width={w}
                height={GLASS_BOTTOM - GLASS_TOP}
                rx="8"
                fill="var(--kombi-glass)"
              />
              {/* The diagonal shading every pane carries: a darker wedge anchored on the pane's LEFT
                  edge, 42% of its width at the top and pinching out at the bottom. Traced off a
                  luminance map — the first pass put a LIGHTER wedge on the right, i.e. inverted it. */}
              <path
                d={`M ${x} ${GLASS_TOP} H ${x + w * 0.42} L ${x} ${GLASS_BOTTOM} Z`}
                fill="var(--kombi-glass-shade)"
              />
            </g>
          ))}
        </g>

        {/* Air louvres on the rear panel — six lines, full 51-unit width. */}
        <g stroke="var(--kombi-glass-shade)" strokeWidth="4">
          {LOUVRES.map((y) => (
            <path key={y} d={`M ${LOUVRE_X1} ${y} H ${LOUVRE_X2}`} />
          ))}
        </g>

        {/* The headlight: a cream oval sitting IN the teal below the nose wedge's point, with a dark
            crescent down its inboard side. The crescent is an ink oval offset 4 units left and drawn
            first, so the cream one covers all but its left edge. */}
        <ellipse cx="1050" cy="582" rx="12" ry="21" fill="var(--kombi-glass-shade)" />
        <ellipse cx="1054" cy="582" rx="9" ry="20" fill="var(--kombi-shell)" />

        {/* Bumpers and the door handle. Thin flat bars, not the chunky rounded blocks of the first pass:
            rear x 459..493 y 627..642, front x 1031..1082 y 626..643, handle x 901..923 y 516..520. */}
        <g fill="var(--kombi-shell)">
          <rect x="459" y="627" width="34" height="15" rx="4" />
          <rect x="1031" y="626" width="51" height="17" rx="4" />
          <rect x="901" y="516" width="22" height="4" rx="2" />
        </g>
      </g>

      <Wheel cx={WHEEL_REAR_X} />
      <Wheel cx={WHEEL_FRONT_X} />
    </>
  );
}

/**
 * Kombi. See the file header for why this is a rebuilt SVG rather than the supplied PNG, and kombi.css
 * for the palette and the parallax rates.
 */
export default function Kombi({
  variant = 'scene',
  motion = 'drive',
  speed = 1,
  parked = false,
  className = '',
  title,
  tint,
}: KombiProps) {
  /**
   * The <defs> ids must be unique per DOCUMENT, not per component — two Kombis on one page sharing an
   * id would both instance whichever tile mounted first. useId gives a stable, SSR-safe namespace; it
   * emits colons, which are legal in an id but not in a CSS selector, so nothing here selects on them.
   */
  const uid = useId();
  const id = (name: string) => `kombi-${uid}-${name}`;

  /**
   * Clamped because every duration in kombi.css is `calc(<time> / var(--kombi-speed))`: a zero would
   * divide by zero and a negative would yield a negative duration, and in both cases EVERY animation
   * on the element becomes invalid at once. The ceiling is not a correctness matter, just a refusal to
   * render something that reads as a fault rather than as fast.
   */
  const safeSpeed = Number.isFinite(speed) ? Math.min(Math.max(speed, 0.1), 8) : 1;
  const style = { '--kombi-speed': String(safeSpeed), ...tint } as CSSProperties;

  /* Labelled or decorative — see the header's ACCESSIBILITY note. */
  const a11y = title
    ? { role: 'img' as const, 'aria-label': title }
    : { 'aria-hidden': true as const };

  const viewBox =
    variant === 'scene'
      ? SCENE_VIEWBOX
      : variant === 'band'
        ? BAND_VIEWBOX
        : parked
          ? BUS_PARKED_VIEWBOX
          : BUS_VIEWBOX;

  return (
    <svg
      className={`tb-kombi tb-kombi--${variant} ${className}`.trim()}
      data-motion={motion}
      data-parked={parked ? 'true' : undefined}
      data-testid="kombi"
      style={style}
      viewBox={viewBox}
      /* scene/band: cover the box and crop from the TOP, so a host that forces a shorter height loses
         sky rather than squashing the bus or cutting the road off. bus: the crop is already tight to the
         vehicle, so it letterboxes instead. */
      preserveAspectRatio={variant === 'bus' ? 'xMidYMid meet' : 'xMidYMax slice'}
      focusable="false"
      {...a11y}
    >
      {title ? <title>{title}</title> : null}

      {variant === 'scene' && (
        <>
          <defs>
            <g id={id('clouds')}>
              <CloudTile />
            </g>
            <g id={id('hills')}>
              <HillTile />
            </g>
            <g id={id('trees')}>
              <TreeTile groundY={HORIZON} />
            </g>
            <g id={id('dashes')}>
              <DashTile />
            </g>
          </defs>

          {/* The sun does NOT scroll. It is the one object at effectively infinite distance, so a
              parallax rate for it would be zero — and one sun per tile would mean a procession of suns
              crossing the frame, which is the tiling scheme becoming visible in the worst way.
              Measured: centre (209, 260), r 67. */}
          <circle cx="209" cy="260" r="67" fill="var(--kombi-lime)" />

          {/* Every scrolling layer is the same shape: its tile, twice, side by side. Back to front. */}
          <g className="tb-kombi__clouds">
            <use href={`#${id('clouds')}`} />
            <use href={`#${id('clouds')}`} x={TILE} />
          </g>
          <g className="tb-kombi__hills">
            <use href={`#${id('hills')}`} />
            <use href={`#${id('hills')}`} x={TILE} />
          </g>
          <g className="tb-kombi__trees">
            <use href={`#${id('trees')}`} />
            <use href={`#${id('trees')}`} x={TILE} />
          </g>

          {/* The road band itself is static — it is a flat colour, so scrolling it would cost frames
              and change nothing. Only its markings carry the speed. */}
          <rect x="0" y={HORIZON} width={TILE} height={FLOOR - HORIZON} fill="var(--kombi-sand)" />
          <g className="tb-kombi__dashes">
            <use href={`#${id('dashes')}`} />
            <use href={`#${id('dashes')}`} x={ROAD_TILE} />
          </g>

          {/* No speed streaks on a parked van — nothing is whooshing past it. */}
          {parked ? <ParkingSign shadow /> : <Streaks />}
          <g className="tb-kombi__enter">
            <Bus clipId={id('shell')} />
          </g>
        </>
      )}

      {variant === 'band' && (
        <>
          <defs>
            <g id={id('trees')}>
              {/* No ground in this variant, so the trunks run to the strip's floor. */}
              <TreeTile groundY={BUS_FLOOR} />
            </g>
          </defs>

          {/* THREE instances, not two: the box is two tiles wide, so covering it throughout a one-tile
              scroll takes ceil(2) + 1. Instances at 0/1536/3072 span 0..4608, and after translating
              -1536 they span -1536..3072 — the visible 0..3072 stays covered at both ends of the
              cycle, which is what makes the loop seamless at this width. */}
          <g className="tb-kombi__trees">
            {Array.from({ length: BAND_TILES + 1 }, (_, i) => (
              <use key={i} href={`#${id('trees')}`} x={i * TILE} />
            ))}
          </g>

          {/* The bus and its streaks, centred in the doubled box. Both move together because the
              streaks belong to the vehicle, not the world. */}
          <g transform={`translate(${BAND_BUS_SHIFT} 0)`}>
            {parked ? <ParkingSign shadow={false} /> : <Streaks />}
            <g className="tb-kombi__enter">
              <Bus clipId={id('shell')} shadow={false} />
            </g>
          </g>
        </>
      )}

      {variant === 'bus' && (
        <>
          {parked ? <ParkingSign shadow /> : null}
          <g className="tb-kombi__enter">
            <Bus clipId={id('shell')} />
          </g>
        </>
      )}
    </svg>
  );
}
